import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, CheckCircle2, XCircle, CalendarClock, User, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminScheduleChanges() {
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [approveFor, setApproveFor] = useState(null);
  const [note, setNote] = useState("");
  const [override, setOverride] = useState({ date: "", time: "" });

  const load = async () => {
    setRows(null);
    try { setRows((await axios.get(`${API}/admin/schedule-changes?status=${filter}`)).data); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not load"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const doApprove = async () => {
    setBusyId(approveFor.id);
    try {
      const body = { admin_note: note.trim() || undefined };
      if (override.date) body.date = override.date;
      if (override.time) body.time = override.time;
      await axios.post(`${API}/admin/schedule-changes/${approveFor.id}/approve`, body);
      toast.success("Approved");
      setApproveFor(null); setNote(""); setOverride({ date: "", time: "" });
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not approve"); }
    finally { setBusyId(null); }
  };

  const doReject = async () => {
    if (note.trim().length < 3) return toast.error("Please give a reason");
    setBusyId(rejectFor.id);
    try {
      await axios.post(`${API}/admin/schedule-changes/${rejectFor.id}/reject`, { admin_note: note.trim() });
      toast.success("Rejected");
      setRejectFor(null); setNote("");
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not reject"); }
    finally { setBusyId(null); }
  };

  return (
    <div data-testid="admin-schedule-changes-page">
      <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">Schedule Change Requests</h1>
      <p className="text-[#3f4b52] mt-1">Fitters can request to move their assigned jobs. You have the final say.</p>

      <div className="mt-4 inline-flex bg-white border border-[#cfd7db] rounded-md p-1" data-testid="scr-filter">
        {["pending", "approved", "rejected", "all"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`scr-filter-${f}`}
                  className={`px-3 py-1.5 text-xs font-display uppercase tracking-widest rounded ${filter === f ? "bg-[#f28a16] text-white" : "text-[#3f4b52]"}`}>
            {f}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-3">
        {!rows && <div className="text-sm text-[#3f4b52]"><Loader2 className="inline animate-spin" /> Loading…</div>}
        {rows && rows.length === 0 && (
          <div className="bg-white border border-dashed border-[#cfd7db] rounded-md p-10 text-center text-[#3f4b52]" data-testid="scr-empty">
            No requests in this bucket.
          </div>
        )}
        {rows?.map((r) => (
          <div key={r.id} data-testid={`scr-row-${r.id}`} className="bg-white border border-[#cfd7db] rounded-md p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-xs text-[#3f4b52] font-display uppercase tracking-widest">
                  Job #{r.job_number} · Requested {new Date(r.created_at).toLocaleString("en-ZA")}
                </div>
                <div className="font-display font-bold text-[#111619] text-lg mt-0.5 inline-flex items-center gap-2">
                  <User size={16} className="text-[#f28a16]" /> {r.fitter_name}
                </div>
                <div className="mt-1 text-sm text-[#111619]">
                  <span className="line-through text-[#9aa6ad]">{r.old_date} {r.old_time || ""}</span>
                  {" → "}
                  <b>{r.new_date} {r.new_time || ""}</b>
                </div>
                <div className="mt-1 text-xs text-[#3f4b52]">Reason: {r.reason}</div>
                {r.status !== "pending" && (
                  <div className={`mt-2 inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-xs font-display uppercase tracking-widest ${r.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                    {r.status === "approved" ? <CheckCircle2 size={12} /> : <XCircle size={12} />} {r.status} · {r.admin_note || ""}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Link to={`/admin/jobs/${r.job_id}`} className="text-xs text-[#f28a16] hover:underline">Open job</Link>
                {r.status === "pending" && (
                  <>
                    <button onClick={() => setRejectFor(r)} data-testid={`scr-reject-${r.id}`}
                            className="inline-flex items-center gap-1 border border-red-300 text-red-700 rounded-md px-3 py-1.5 text-xs font-display uppercase tracking-widest">
                      <XCircle size={12} /> Reject
                    </button>
                    <button onClick={() => { setApproveFor(r); setOverride({ date: r.new_date, time: r.new_time || "" }); }}
                            data-testid={`scr-approve-${r.id}`}
                            className="inline-flex items-center gap-1 bg-[#f28a16] text-white rounded-md px-3 py-1.5 text-xs font-display uppercase tracking-widest">
                      <CalendarClock size={12} /> Approve
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {approveFor && (
        <Modal onClose={() => setApproveFor(null)} testId="scr-approve-modal">
          <div className="font-display uppercase tracking-widest text-sm">Approve schedule change · Job #{approveFor.job_number}</div>
          <p className="text-xs text-[#3f4b52]">You may adjust the fitter&apos;s proposed date/time. A conflict check will block clashes with the same fitter.</p>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-xs font-display uppercase tracking-widest block mb-1">Date</span>
              <input type="date" value={override.date} onChange={(e) => setOverride({ ...override, date: e.target.value })}
                     data-testid="scr-approve-date" className="w-full h-10 border rounded-md px-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-display uppercase tracking-widest block mb-1">Time</span>
              <input type="time" value={override.time} onChange={(e) => setOverride({ ...override, time: e.target.value })}
                     data-testid="scr-approve-time" className="w-full h-10 border rounded-md px-2 text-sm" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-display uppercase tracking-widest block mb-1">Note (optional)</span>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
                      data-testid="scr-approve-note" className="w-full border rounded-md p-2 text-sm" />
          </label>
          <div className="flex gap-2">
            <button onClick={() => setApproveFor(null)} className="flex-1 border rounded-md py-2 text-sm">Cancel</button>
            <button onClick={doApprove} disabled={busyId === approveFor.id}
                    data-testid="scr-approve-confirm"
                    className="flex-1 bg-[#f28a16] text-white rounded-md py-2 text-sm font-display uppercase tracking-widest disabled:opacity-60">
              {busyId === approveFor.id ? "Approving…" : "Approve"}
            </button>
          </div>
        </Modal>
      )}

      {rejectFor && (
        <Modal onClose={() => setRejectFor(null)} testId="scr-reject-modal">
          <div className="font-display uppercase tracking-widest text-sm inline-flex items-center gap-2">
            <AlertTriangle size={16} className="text-red-600" /> Reject schedule change
          </div>
          <label className="block">
            <span className="text-xs font-display uppercase tracking-widest block mb-1">Reason (min 3 chars) — will be sent to fitter</span>
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} minLength={3} maxLength={500}
                      data-testid="scr-reject-note" className="w-full border rounded-md p-2 text-sm" />
          </label>
          <div className="flex gap-2">
            <button onClick={() => setRejectFor(null)} className="flex-1 border rounded-md py-2 text-sm">Cancel</button>
            <button onClick={doReject} disabled={busyId === rejectFor.id || note.trim().length < 3}
                    data-testid="scr-reject-confirm"
                    className="flex-1 bg-red-600 text-white rounded-md py-2 text-sm font-display uppercase tracking-widest disabled:opacity-60">
              {busyId === rejectFor.id ? "Rejecting…" : "Reject"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const Modal = ({ children, onClose, testId }) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3" onClick={onClose}>
    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md p-5 w-full max-w-md space-y-3" data-testid={testId}>
      {children}
    </div>
  </div>
);
